import { clsx } from 'clsx';

type StepperProps = {
  currentStep: number;
  totalSteps: number;
  labels?: string[];
  className?: string;
};

export function Stepper({ currentStep, totalSteps, labels, className }: StepperProps) {
  return (
    <div
      className={clsx('w-full', className)}
      role="progressbar"
      aria-valuenow={currentStep}
      aria-valuemin={1}
      aria-valuemax={totalSteps}
      aria-label={`Passo ${currentStep} de ${totalSteps}`}
    >
      <ol className="flex items-center gap-0">
        {Array.from({ length: totalSteps }, (_, i) => {
          const stepNum = i + 1;
          const isComplete = stepNum < currentStep;
          const isCurrent = stepNum === currentStep;
          const isFuture = stepNum > currentStep;
          const label = labels?.[i];

          return (
            <li
              key={stepNum}
              className={clsx(
                'flex items-center',
                i < totalSteps - 1 && 'flex-1',
              )}
            >
              <div className="flex flex-col items-center gap-2">
                <span
                  aria-hidden="true"
                  className={clsx(
                    'flex h-10 w-10 items-center justify-center rounded-full font-display text-sm font-semibold transition-all duration-base',
                    isComplete && 'bg-gold-gradient text-night shadow-goldSoft',
                    isCurrent &&
                      'border border-gold bg-night text-gold shadow-goldMedium ring-1 ring-gold/30',
                    isFuture && 'border border-gold/30 bg-night/40 text-champagne/40',
                  )}
                >
                  {isComplete ? (
                    <svg
                      viewBox="0 0 20 20"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 10.5l4 4 8-9" />
                    </svg>
                  ) : (
                    <span>{String(stepNum).padStart(2, '0')}</span>
                  )}
                </span>
                {label ? (
                  <span
                    className={clsx(
                      'font-mono text-[10px] uppercase tracking-stamp',
                      isCurrent ? 'text-champagne' : 'text-champagne/40',
                    )}
                  >
                    {label}
                  </span>
                ) : null}
              </div>

              {i < totalSteps - 1 ? (
                <span
                  aria-hidden="true"
                  className={clsx(
                    'mx-2 h-px flex-1 transition-colors duration-base sm:mx-4',
                    isComplete
                      ? 'bg-gradient-to-r from-gold/70 to-gold/40'
                      : 'bg-gradient-to-r from-gold/40 to-gold/10',
                  )}
                  style={{ marginBottom: label ? '1.75rem' : 0 }}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
